import { useState } from 'react';
import { Link, useNavigate, type LinkProps } from '@tanstack/react-router';
import { Envelope } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { emailSchema, OTP_LENGTH, passwordSchema } from '@mybusinessmate/schema';
import { AuthHeading } from '../auth/auth-heading';
import { BackLink } from '../auth/back-link';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Input, PasswordInput } from '../ui/input';
import { Mascot } from '../ui/mascot';
import { OtpInput } from '../ui/otp-input';
import { PasswordStrength } from '../ui/password-strength';
import { useToast } from '../ui/toast';
import { textLinkClass } from '../ui/text-link';
import { authClient } from '../../lib/api';
import { passwordStrength } from '../../lib/password-strength';
import { formatCountdown, useCountdown } from '../../lib/use-countdown';

type Stage = 'email' | 'code' | 'password' | 'done';
const RESEND_SECONDS = 30;
// Matches the server (emailOTP expiresIn: 900). The design copy says 10 minutes; the backend is the source of truth.
const CODE_VALID_MINUTES = 15;

export interface ForgotPasswordFlowProps {
  /** The sign-in screen of whichever app this runs in (client or staff). */
  signInTo: LinkProps['to'];
}

/**
 * Reset-by-code flow shared by the client and staff apps: email -> 6-digit code -> new password -> done.
 * The API answers the first step the same whether or not the account exists.
 */
export function ForgotPasswordFlow({ signInTo }: ForgotPasswordFlowProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { remaining, done: canResend, restart } = useCountdown(RESEND_SECONDS);

  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const sendCode = async (): Promise<boolean> => {
    setError(undefined);
    const parsed = emailSchema.safeParse(email.trim());
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message);
      return false;
    }
    setBusy(true);
    // The API answers the same whether or not the account exists, so this never reveals who has one.
    const { error: authError } = await authClient.emailOtp.requestPasswordReset({ email: parsed.data });
    setBusy(false);
    const mapped = mapAuthError(authError);
    if (mapped) {
      setError(
        mapped.kind === 'rate_limited' ? 'Please wait a moment before asking for another code.' : mapped.message,
      );
      return false;
    }
    setEmail(parsed.data);
    restart();
    return true;
  };

  const checkCode = async (code: string) => {
    setBusy(true);
    setError(undefined);
    const { error: authError } = await authClient.emailOtp.checkVerificationOtp({
      email,
      type: 'forget-password',
      otp: code,
    });
    setBusy(false);
    const mapped = mapAuthError(authError);
    if (mapped) {
      setError(
        mapped.kind === 'invalid_code'
          ? 'That code didn’t work. Check it and try again.'
          : mapped.kind === 'too_many_attempts'
            ? 'Too many wrong codes. Request a new one.'
            : mapped.message,
      );
      return;
    }
    setStage('password');
  };

  const savePassword = async () => {
    setError(undefined);
    const valid = passwordSchema.safeParse(password);
    if (!valid.success) return setError(valid.error.issues[0]?.message);
    if (password !== confirm) return setError('Passwords do not match');
    setBusy(true);
    const { error: authError } = await authClient.emailOtp.resetPassword({ email, otp, password });
    setBusy(false);
    const mapped = mapAuthError(authError);
    if (mapped) {
      if (mapped.kind === 'invalid_code' || mapped.kind === 'too_many_attempts') {
        // The code went stale while the user was typing a password: start the code step again.
        setOtp('');
        setStage('code');
      }
      return setError(mapped.message);
    }
    setStage('done');
  };

  if (stage === 'done') {
    return (
      <>
        <Mascot pose="cheer" className="h-(--mbm-size-mascot-height) w-(--mbm-size-mascot-width) self-center" />
        <AuthHeading
          title="Password updated"
          subtitle="You’re all set. Sign in with your new password to get back to work."
        />
        <Button fullWidth onClick={() => navigate({ to: signInTo })}>
          Sign in
        </Button>
      </>
    );
  }

  if (stage === 'password') {
    const strength = passwordStrength(password);
    const matches = confirm.length > 0 && password === confirm;
    return (
      <>
        <AuthHeading title="Set a new password" subtitle="At least 8 characters, with a number or symbol." />
        {error ? <Banner tone="error">{error}</Banner> : null}
        <div className="flex flex-col gap-(--mbm-space-3)">
          <PasswordInput
            label="New password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordStrength level={strength} />
        </div>
        <PasswordInput
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          helperText={matches ? 'Passwords match' : undefined}
          error={confirm.length > 0 && !matches ? 'Passwords do not match' : undefined}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button fullWidth loading={busy} disabled={!password || !matches} onClick={() => void savePassword()}>
          Save password
        </Button>
      </>
    );
  }

  if (stage === 'code') {
    return (
      <>
        <AuthHeading
          title="Enter the reset code"
          subtitle={`We sent a 6-digit code to ${email}. It works for ${CODE_VALID_MINUTES} minutes.`}
        />
        <div className="flex flex-col gap-(--mbm-space-3)">
          <OtpInput
            value={otp}
            onChange={(value) => {
              setOtp(value);
              setError(undefined);
            }}
            onComplete={(code) => void checkCode(code)}
            disabled={busy}
            error={Boolean(error)}
            autoFocus
          />
          {error ? (
            <p role="alert" className="text-body-small text-feedback-error">
              {error}
            </p>
          ) : null}
        </div>
        <p className="flex justify-center gap-(--mbm-space-1) text-body-small text-text-secondary">
          Didn’t get it?{' '}
          {canResend ? (
            <button
              type="button"
              className={textLinkClass}
              onClick={async () => {
                setOtp('');
                if (await sendCode()) toast({ title: 'New code sent', description: `Check ${email}` });
              }}
            >
              Resend code
            </button>
          ) : (
            <span className="text-label-medium text-brand-primary">Resend code in {formatCountdown(remaining)}</span>
          )}
        </p>
        <Button fullWidth disabled={otp.length !== OTP_LENGTH} loading={busy} onClick={() => void checkCode(otp)}>
          Continue
        </Button>
      </>
    );
  }

  return (
    <form
      noValidate
      className="flex flex-col gap-(--mbm-space-6)"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await sendCode()) setStage('code');
      }}
    >
      <BackLink to={signInTo}>Back to sign in</BackLink>
      <AuthHeading
        title="Forgot your password?"
        subtitle="Enter your account email. We’ll send a 6-digit code to reset it."
      />
      <Input
        label="Email"
        type="email"
        autoComplete="email"
        leadingIcon={Envelope}
        value={email}
        error={error}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Button type="submit" fullWidth loading={busy}>
        Send code
      </Button>
      <p className="text-center text-body-small text-text-secondary">
        Remembered it?{' '}
        <Link to={signInTo} className={textLinkClass}>
          Back to sign in
        </Link>
      </p>
    </form>
  );
}
